import { useContext } from "react";
import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { RegMark } from "./Circles";
import { openContact } from "./ContactDialog";
import { ContentCtx } from "@/lib/content";

const DEFAULT_LINES = [
  { text: "Let Your" },
  { text: "Brand" },
  { text: "Speak", pink: true },
  { text: "For Itself." },
];
const DEFAULT_SUB =
  "STAX MARKETING LLC is a creative design & marketing studio helping businesses build brands that look as good as they perform.";

export const Hero = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const tiltX = useMotionValue(0);
  const tiltY = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 40, damping: 14 });
  const sy = useSpring(my, { stiffness: 40, damping: 14 });
  const rX = useSpring(tiltX, { stiffness: 55, damping: 16 });
  const rY = useSpring(tiltY, { stiffness: 55, damping: 16 });
  const { scrollY } = useScroll();
  const drift = useTransform(scrollY, [0, 600], [0, 120]);
  const content = useContext(ContentCtx);
  const lines = content?.hero?.lines?.length ? content.hero.lines : DEFAULT_LINES;
  const sub = content?.hero?.sub || DEFAULT_SUB;

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    mx.set(px * 26);
    my.set(py * 26);
    tiltX.set(-py * 9);
    tiltY.set(px * 11);
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      onMouseMove={onMove}
      className="relative overflow-hidden bg-paper pt-[72px]"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-20"
        style={{
          background: "radial-gradient(520px circle at var(--mx, 70%) var(--my, 30%), rgba(255,255,255,0.55), transparent 62%)",
          mixBlendMode: "soft-light",
        }}
      />
      <div className="halftone absolute right-0 top-[72px] h-64 w-64 opacity-20 pointer-events-none" />
      <RegMark className="absolute left-6 top-28 text-ink/30 hidden md:block" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 lg:grid-cols-12 gap-10 px-4 sm:px-8 pb-20 pt-14 sm:pt-20">
        <div className="lg:col-span-7 relative z-10">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.7 }}
            className="mb-6 inline-flex items-center gap-2 border-2 border-ink bg-white px-3 py-1.5 font-mono2 text-xs font-bold uppercase tracking-[0.25em]"
          >
            <span className="h-2.5 w-2.5 rounded-full bg-staxmagenta" />
            Graphic Design + Marketing Studio
          </motion.p>

          <h1 className="font-display uppercase leading-[0.88] text-ink" style={{ fontSize: "clamp(2.9rem, 8.6vw, 7.4rem)" }}>
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  className={`block ${line.pink ? "text-staxmagenta" : ""}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.8, delay: 1.8 + 0.12 * i, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 2.5 }}
            className="mt-7 max-w-md text-base sm:text-lg font-medium text-ink/70"
          >
            {sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 2.65 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-work-together"
              onClick={() => openContact()}
              className="group inline-flex items-center gap-2 border-2 border-ink bg-staxmagenta px-7 py-4 font-display uppercase text-lg text-white shadow-hard transition-transform hover:-translate-y-1"
            >
              Let's Work Together <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
            </button>
            {!!content?.sections?.work && (
              <a
                data-testid="hero-cta-view-work"
                href="#work"
                className="inline-flex items-center gap-2 border-2 border-ink bg-white px-7 py-4 font-display uppercase text-lg text-ink transition-colors hover:bg-staxyellow"
              >
                View The Work <ArrowDown size={20} />
              </a>
            )}
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative min-h-[340px] lg:min-h-[520px]">
          <motion.div style={{ x: sx, y: sy, rotateX: rX, rotateY: rY, transformPerspective: 1000 }} className="absolute inset-0">
            <motion.div
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.9, delay: 2.0, ease: [0.22, 1, 0.36, 1] }}
              className="relative h-full w-full"
            >
              <motion.div style={{ y: drift }} className="ink-dot absolute -right-24 -top-10 h-72 w-72 sm:h-96 sm:w-96 bg-staxyellow" />
              <div className="ink-dot absolute right-10 top-40 h-52 w-52 sm:h-64 sm:w-64 bg-staxcyan" />
              <div className="ink-dot absolute right-40 top-64 h-40 w-40 sm:h-52 sm:w-52 bg-staxmagenta" />
              <div className="absolute right-8 top-24 sm:top-32 border-4 border-ink bg-white px-6 py-5 shadow-hard rotate-[-3deg]">
                <img src="/logo-vertical.png" alt="STAX MARKETING" className="w-32 sm:w-40" />
              </div>
              <div className="spin-slow absolute left-2 bottom-6 hidden sm:block">
                <svg width="110" height="110" viewBox="0 0 110 110">
                  <defs>
                    <path id="circ" d="M55,55 m-42,0 a42,42 0 1,1 84,0 a42,42 0 1,1 -84,0" />
                  </defs>
                  <circle cx="55" cy="55" r="54" fill="var(--ink)" />
                  <text fill="var(--sy)" fontSize="12.5" fontFamily="'Space Mono', monospace" fontWeight="700" letterSpacing="3">
                    <textPath href="#circ" textLength="261" lengthAdjust="spacingAndGlyphs">THE BOLDER THE BETTER •</textPath>
                  </text>
                </svg>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
